import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Article, ArticlePost, ArticlePublish, ArticleUpdate } from './types';
import { DateTime } from 'luxon';
import { catchError } from 'rxjs/operators';
import { ErrorService } from '../../error';
import { MessageService } from '../../service/message/message.service';
import { CrudType } from '../../service/message/types';

@Injectable({
  providedIn: 'root',
})
export class ArticleApiService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly errorService: ErrorService,
    private readonly messageService: MessageService,
  ) {}

  getById = (id: string): Observable<Article> => {
    let parameters: string[];
    parameters = [id];
    let url = this.formatUrl(parameters);
    return this.httpClient.get<Article>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  postArticle = (articlePost: ArticlePost): any => {
    let parameters: string[];
    parameters = [];
    let url = this.formatUrl(parameters);

    let formData = new FormData();
    formData.append('name', articlePost.name);
    formData.append('url', articlePost.url);
    formData.append('description', articlePost.description);
    formData.append('pagecontent', articlePost.pageContent);
    formData.append('thumbnail', articlePost.thumbnail);
    formData.append('header', articlePost.header);
    formData.append('imageContent', articlePost.imageContent);

    return this.httpClient
      .post<Article>(url, formData, { observe: 'response' })
      .pipe(
        this.messageService.checkStatusCustomOperator(CrudType.create),
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  publishArticle = (id: string): any => {
    let parameters: string[];
    parameters = ['publish', id];
    let url = this.formatUrl(parameters);
    let articlePut: ArticlePublish = {
      publicationDate: DateTime.utc(),
    };
    return this.httpClient.put<Article>(url, articlePut).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  unpublishArticle = (id: string): any => {
    let parameters: string[];
    parameters = ['unpublish', id];
    let url = this.formatUrl(parameters);
    return this.httpClient.put<Article>(url, '').pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  updateArticle = (articleUpdate: ArticleUpdate): any => {
    let parameters: string[];
    parameters = [articleUpdate.id];
    let url = this.formatUrl(parameters);

    let formData = new FormData();
    formData.append('name', articleUpdate.name);
    formData.append('url', articleUpdate.url);
    formData.append('description', articleUpdate.description);
    formData.append('pagecontent', articleUpdate.pageContent);
    if (articleUpdate.thumbnail) {
      formData.append('thumbnail', articleUpdate.thumbnail);
    }
    if (articleUpdate.header) {
      formData.append('header', articleUpdate.header);
    }
    if (articleUpdate.imageContent) {
      formData.append('imageContent', articleUpdate.imageContent);
    }

    return this.httpClient
      .put<Article>(url, formData, { observe: 'response' })
      .pipe(
        this.messageService.checkStatusCustomOperator(CrudType.update),
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  delete = (id: string): Observable<boolean> => {
    let parameters: string[];
    parameters = [id];
    let url = this.formatUrl(parameters);

    return this.httpClient.delete<boolean>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  formatUrl = (parameters: string[]): string => {
    let url = environment.comparanooapiurl;
    url += environment.features.articles.url;
    parameters.forEach((parameters) => {
      url += '/';
      url += parameters;
    });
    return url;
  };
}
